import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, CircleUser } from 'lucide-react'
import { CockpitMark } from './CockpitMark'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { COCKPIT_ROLES, currentUser, decodeRoles, signOut } from '../lib/auth'
import { t, tf, type Lang, type TranslationKey } from '../lib/i18n'
import { useHasInAppPrevious, useInAppBack } from '../lib/in-app-history'
import { landSignedOut, sendQueuedThenCount } from '../lib/shell-clear'

interface Props {
  title: string
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
  showHome?: boolean
  // R492: the Back is decided from the route and the app's own stack (see showsBack); false
  // hides it on a screen the person must not leave by Back (the forced password change).
  showBack?: boolean
  titleTestId?: string
}

// R492: the screens Back does not apply to on their own. The cockpit is home and never has one;
// the Board and the kitchen screens are where a person lands, so they show Back only when HOP
// itself brought the person there. Every other screen is a sub-screen and always shows it; with
// an empty stack it goes to the Board.
const HOME = '/cockpit'
const LANDING = new Set(['/board', '/kitchen', '/kitchen/tv'])

// Rook #66472: a person who cannot open the Board (a kitchen-only login) never gets a Back that
// would fall back to it; they keep a Back only when HOP itself brought them to the screen.
export function showsBack(pathname: string, hasPrevious: boolean, canOpenBoard = true): boolean {
  if (pathname === HOME) return false
  if (LANDING.has(pathname) || !canOpenBoard) return hasPrevious
  return true
}

// R95 — the one dark bar the six command-center screens share: home mark, title,
// online dot, language toggle, account menu (username, role, Sign out).
// R492 (Dee via Rook #66417): it is THE shell every HOP screen draws, with a Back on every
// sub-screen that walks the app's own stack (lib/in-app-history.ts).
// Ranked: the first present of owner, admin, hop, architect.
const ROLE_LABELS: ReadonlyArray<readonly [string, TranslationKey]> = [
  ['owner', 'roleOwner'],
  ['admin', 'roleAdmin'],
  ['hop', 'roleHop'],
  ['architect', 'roleArchitect'],
]

export function CommandHeader({ title, lang, onLangToggle, isOnline, showHome = true, showBack, titleTestId }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const hasPrevious = useHasInAppPrevious(location.key)
  const handleBack = useInAppBack()
  const canOpenBoard = decodeRoles().some((r) => (COCKPIT_ROLES as readonly string[]).includes(r))
  const backShown = showBack ?? showsBack(location.pathname, hasPrevious, canOpenBoard)
  const [signOutError, setSignOutError] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const user = currentUser()
  const roleKey = user ? ROLE_LABELS.find(([role]) => user.roles.includes(role))?.[1] : undefined

  // R493 (Frank #66467): a manual sign-out SENDS this person's queued writes first (the session
  // is still alive); writes that still cannot go are counted and the person is asked.
  const [unsent, setUnsent] = useState<number | null>(null)

  async function finishSignOut() {
    if (await signOut()) {
      // R493: the gateway cleared the cookie; clear HOP's cache and worker, then land.
      await landSignedOut(navigate)
    } else {
      setSigningOut(false)
      setSignOutError(true)
    }
  }

  async function handleSignOut(event: Event) {
    // Keep the menu open: a failed sign-out has to be seen where it was asked for.
    event.preventDefault()
    if (signingOut) return
    setSignOutError(false)
    setUnsent(null)
    setSigningOut(true)
    const waiting = await sendQueuedThenCount()
    if (waiting > 0) {
      setSigningOut(false)
      setUnsent(waiting)
      return
    }
    await finishSignOut()
  }

  async function handleSignOutAnyway(event: Event) {
    event.preventDefault()
    if (signingOut) return
    setUnsent(null)
    setSigningOut(true)
    await finishSignOut()
  }

  return (
    <header
      data-command-header
      className={`bg-primary text-primary-foreground h-16 px-4 flex items-center justify-between gap-3 ${lang === 'ar' ? 'font-arabic' : ''}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {backShown && (
          // The href is the Board (a new tab, or no script, lands there); a tap walks the stack.
          <Link
            to="/board"
            onClick={handleBack}
            data-testid="shell-back"
            aria-label={t(lang, 'back')}
            className="-ms-2 inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1 rounded-md px-2 text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
          >
            <ChevronLeft aria-hidden="true" className="h-5 w-5 rtl:rotate-180" />
            <span className="hidden sm:inline">{t(lang, 'back')}</span>
          </Link>
        )}
        {showHome && <CockpitMark lang={lang} className="text-primary-foreground/80 hover:text-primary-foreground" />}
        <h1 data-testid={titleTestId} className="text-xl font-semibold tracking-wide truncate min-w-0">{title}</h1>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-1.5 min-h-[44px]">
          <span
            data-online-dot
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 transition-colors ${
              isOnline ? 'bg-green-400' : 'bg-red-400'
            }`}
          />
          <span className="text-primary-foreground/80 text-xs hidden sm:inline">
            {t(lang, isOnline ? 'online' : 'offline')}
          </span>
        </div>
        <button
          type="button"
          onClick={onLangToggle}
          className="border border-primary-foreground/40 bg-transparent hover:bg-primary-foreground/10 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] min-w-[64px] transition-colors"
        >
          {t(lang, 'langToggle')}
        </button>
        <DropdownMenu
          onOpenChange={(open) => {
            if (!open) return
            setSignOutError(false)
            setUnsent(null)
          }}
        >
          <DropdownMenuTrigger
            aria-label={t(lang, 'accountMenu')}
            title={t(lang, 'accountMenu')}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-primary-foreground/80 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
          >
            <CircleUser aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[12rem]">
            {user && <DropdownMenuLabel>{user.username}</DropdownMenuLabel>}
            {roleKey && <div className="px-2 pb-1.5 text-xs text-muted-foreground">{t(lang, roleKey)}</div>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleSignOut} disabled={signingOut} className="min-h-[44px]">
              {t(lang, 'signOut')}
            </DropdownMenuItem>
            {signOutError && (
              <p role="alert" className="px-2 py-1.5 text-xs text-destructive">
                {t(lang, 'signOutFailed')}
              </p>
            )}
            {unsent !== null && (
              <div data-testid="sign-out-unsent" className="max-w-[16rem]">
                <p role="alert" className="px-2 py-1.5 text-xs text-destructive">
                  {unsent === 1 ? t(lang, 'signOutUnsentOne') : tf(lang, 'signOutUnsent', { count: unsent })}
                </p>
                <DropdownMenuItem onSelect={handleSignOutAnyway} disabled={signingOut} className="min-h-[44px] text-destructive">
                  {t(lang, 'signOutAnyway')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setUnsent(null)} className="min-h-[44px]">
                  {t(lang, 'signOutStay')}
                </DropdownMenuItem>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
